import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Empty,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  AlertOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  FileTextOutlined,
  LogoutOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  assignAmbulance,
  cancelEMSRequest,
  createAmbulanceUnit,
  getAmbulanceUnits,
  getEMSProfile,
  getEMSRequests,
  getTransportLogs,
  updateAmbulanceUnit,
} from "../../services/emsService";
import { createTicket, getMyTickets } from "../../services/supportService";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString() : "—";
const isToday  = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};

const REQUEST_STATUS_COLOR = {
  PENDING:    "orange",
  DISPATCHED: "blue",
  IN_TRANSIT: "purple",
  COMPLETED:  "green",
  CANCELLED:  "default",
};

const UNIT_STATUS_COLOR = {
  AVAILABLE:      "green",
  DISPATCHED:     "orange",
  OUT_OF_SERVICE: "red",
};

const UNIT_STATUS_BORDER = {
  AVAILABLE:      "#52c41a",
  DISPATCHED:     "#fa8c16",
  OUT_OF_SERVICE: "#f5222d",
};

const MENU_ITEMS = [
  { key: "dashboard",  icon: <DashboardOutlined />, label: "Dashboard" },
  { key: "requests",   icon: <AlertOutlined />,     label: "Active Requests" },
  { key: "units",      icon: <CarOutlined />,        label: "Ambulance Units" },
  { key: "logs",       icon: <FileTextOutlined />,       label: "Transport Logs" },
  { key: "support",    icon: <CustomerServiceOutlined />, label: "Support" },
  { key: "profile",    icon: <UserOutlined />,            label: "My Profile" },
];

const LOG_COLS = [
  { title: "Request ID",    dataIndex: "request",      key: "request",
    render: id => <Text code>{String(id).slice(0,8)}…</Text> },
  { title: "Departed At",   dataIndex: "departed_at",  key: "departed_at",  width: 160, render: fmtTime },
  { title: "Arrived At",    dataIndex: "arrived_at",   key: "arrived_at",   width: 160, render: fmtTime },
  { title: "Condition",     dataIndex: "patient_condition", key: "patient_condition",
    ellipsis: true, render: v => v ?? <Text type="secondary">—</Text> },
];

export default function EMSDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("dashboard");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [retryCount,   setRetryCount]   = useState(0);
  const [submitting,   setSubmitting]   = useState(false);

  const [requests,      setRequests]      = useState([]);
  const [units,         setUnits]         = useState([]);
  const [transportLogs, setTransportLogs] = useState([]);
  const [emsProfile,    setEmsProfile]    = useState(null);
  const [stats,         setStats]         = useState({
    activeRequests: 0, availableUnits: 0, transportedToday: 0, totalUnits: 0,
  });

  // Modal state
  const [dispatchTarget, setDispatchTarget] = useState(null);  // EMSRequest being dispatched
  const [detailTarget,   setDetailTarget]   = useState(null);  // EMSRequest being viewed
  const [addUnitOpen,    setAddUnitOpen]    = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState(null);

  const [addUnitForm] = Form.useForm();
  const refreshTimer  = useRef(null);

  // support
  const [myTickets,      setMyTickets]      = useState([]);
  const [ticketModal,    setTicketModal]    = useState(false);
  const [ticketDetail,   setTicketDetail]   = useState(null);
  const [supportLoaded,  setSupportLoaded]  = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [ticketForm]                        = Form.useForm();

  // ── data fetchers ─────────────────────────────────────────────────────────────

  const fetchRequests = useCallback(async () => {
    const res  = await getEMSRequests();
    const rows = toArray(res.data);
    setRequests(rows.map(r => ({ ...r, key: r.id })));
    return rows;
  }, []);

  const fetchUnits = useCallback(async () => {
    const res  = await getAmbulanceUnits();
    const rows = toArray(res.data);
    setUnits(rows.map(r => ({ ...r, key: r.id })));
    return rows;
  }, []);

  const fetchLogs = useCallback(async () => {
    const res  = await getTransportLogs();
    const rows = toArray(res.data);
    setTransportLogs(rows.map(r => ({ ...r, key: r.id })));
    return rows;
  }, []);

  const rebuildStats = useCallback((reqs, unitRows, logs) => {
    setStats({
      activeRequests:  reqs.filter(r  => ["PENDING","DISPATCHED","IN_TRANSIT"].includes(r.status)).length,
      availableUnits:  unitRows.filter(u => u.current_status === "AVAILABLE").length,
      transportedToday: logs.filter(l => isToday(l.departed_at)).length,
      totalUnits:      unitRows.length,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const [reqRes, unitRes, logRes, profRes] = await Promise.all([
          getEMSRequests(), getAmbulanceUnits(), getTransportLogs(), getEMSProfile(),
        ]);
        if (cancelled) return;
        const reqs  = toArray(reqRes.data);
        const unitRows = toArray(unitRes.data);
        const logs  = toArray(logRes.data);
        const prof  = Array.isArray(profRes.data)
          ? profRes.data[0]
          : (profRes.data?.results?.[0] ?? profRes.data ?? null);
        setRequests(reqs.map(r => ({ ...r, key: r.id })));
        setUnits(unitRows.map(r => ({ ...r, key: r.id })));
        setTransportLogs(logs.map(r => ({ ...r, key: r.id })));
        setEmsProfile(prof);
        rebuildStats(reqs, unitRows, logs);
      } catch {
        if (!cancelled) setError("Failed to load dashboard data. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();

    // Auto-refresh active requests every 30 seconds
    refreshTimer.current = setInterval(async () => {
      try {
        const reqs = await fetchRequests();
        setStats(s => ({
          ...s,
          activeRequests: reqs.filter(r => ["PENDING","DISPATCHED","IN_TRANSIT"].includes(r.status)).length,
        }));
      } catch (err) {
        if (err?.response?.status === 401) {
          clearInterval(refreshTimer.current);
        }
        // otherwise silent — keep existing data
      }
    }, 30000);

    return () => {
      cancelled = true;
      clearInterval(refreshTimer.current);
    };
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── handlers ──────────────────────────────────────────────────────────────────

  const handleDispatch = async () => {
    if (!selectedUnitId || !dispatchTarget) return;
    setSubmitting(true);
    try {
      await assignAmbulance(dispatchTarget.id, selectedUnitId);
      message.success(`Ambulance dispatched to request ${String(dispatchTarget.id).slice(0,8).toUpperCase()}`);
      setDispatchTarget(null);
      setSelectedUnitId(null);
      const [reqs, unitRows, logs] = await Promise.all([fetchRequests(), fetchUnits(), fetchLogs()]);
      rebuildStats(reqs, unitRows, logs);
    } catch (err) {
      message.error(err?.response?.data?.detail ?? err?.response?.data?.assigned_unit?.[0] ?? "Dispatch failed.");
    } finally { setSubmitting(false); }
  };

  const handleCancel = async (req) => {
    Modal.confirm({
      title:   "Cancel EMS Request",
      content: `Cancel request ${String(req.id).slice(0,8).toUpperCase()}? The patient will be notified and any assigned unit will be freed.`,
      okText:  "Cancel Request",
      okType:  "danger",
      onOk: async () => {
        try {
          await cancelEMSRequest(req.id);
          message.success("Request cancelled.");
          setDetailTarget(null);
          const [reqs, unitRows, logs] = await Promise.all([fetchRequests(), fetchUnits(), fetchLogs()]);
          rebuildStats(reqs, unitRows, logs);
        } catch (err) {
          message.error(err?.response?.data?.detail ?? "Failed to cancel request.");
        }
      },
    });
  };

  const handleUnitStatusChange = async (unit, newStatus) => {
    try {
      await updateAmbulanceUnit(unit.id, { current_status: newStatus });
      message.success(`Unit ${unit.unit_number} status updated to ${newStatus}.`);
      const [unitRows] = await Promise.all([fetchUnits()]);
      setStats(s => ({
        ...s,
        availableUnits: unitRows.filter(u => u.current_status === "AVAILABLE").length,
        totalUnits:     unitRows.length,
      }));
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to update unit status.");
    }
  };

  const handleAddUnit = async (values) => {
    setSubmitting(true);
    try {
      await createAmbulanceUnit({
        unit_number:    values.unit_number,
        license_plate:  values.license_plate,
        current_status: values.current_status ?? "AVAILABLE",
      });
      message.success(`Unit ${values.unit_number} added.`);
      setAddUnitOpen(false); addUnitForm.resetFields();
      const unitRows = await fetchUnits();
      setStats(s => ({
        ...s,
        availableUnits: unitRows.filter(u => u.current_status === "AVAILABLE").length,
        totalUnits:     unitRows.length,
      }));
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.unit_number?.[0] ?? d?.license_plate?.[0] ?? d?.detail ?? "Failed to add unit.");
    } finally { setSubmitting(false); }
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const userMenuItems = [
    { key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true, onClick: handleLogout },
  ];

  // ── support ───────────────────────────────────────────────────────────────────
  const loadSupport = useCallback(async () => {
    setSupportLoading(true);
    try {
      const res = await getMyTickets();
      setMyTickets(toArray(res.data).map(r=>({...r,key:r.id})));
      setSupportLoaded(true);
    } catch { /* silent */ } finally { setSupportLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedMenu === "support" && !supportLoaded) loadSupport();
  }, [selectedMenu, supportLoaded, loadSupport]);

  const handleCreateTicket = async (values) => {
    try {
      await createTicket(values);
      message.success("Ticket submitted successfully.");
      setTicketModal(false);
      ticketForm.resetFields();
      loadSupport();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to submit ticket.");
    }
  };

  // ── derived data ──────────────────────────────────────────────────────────────

  const activeRequests   = requests.filter(r => ["PENDING","DISPATCHED","IN_TRANSIT"].includes(r.status));
  const availableUnits   = units.filter(u => u.current_status === "AVAILABLE");
  const activeCount      = activeRequests.filter(r => r.status === "PENDING").length;

  const statCards = [
    { title: "Active Requests",    value: stats.activeRequests,  color: "#f5222d" },
    { title: "Available Units",    value: stats.availableUnits,  color: "#52c41a" },
    { title: "Transported Today",  value: stats.transportedToday,color: "#1677ff" },
    { title: "Total Units",        value: stats.totalUnits,       color: "#722ed1" },
  ];

  // ── column definitions ────────────────────────────────────────────────────────

  const requestColumns = (showActions = true) => [
    { title: "Request ID",   key: "id",            width: 110,
      render: (_, r) => <Text code style={{ fontSize: 11 }}>{String(r.id).slice(0,8).toUpperCase()}</Text> },
    { title: "Pickup Address", dataIndex: "pickup_address", key: "pickup_address", ellipsis: true,
      render: v => v ?? <Text type="secondary">—</Text> },
    { title: "Reason",       dataIndex: "reason",   key: "reason",   ellipsis: true,
      render: v => v ?? <Text type="secondary">—</Text> },
    { title: "Status",       dataIndex: "status",   key: "status",   width: 115,
      render: s => <Tag color={REQUEST_STATUS_COLOR[s] ?? "default"}>{s}</Tag> },
    { title: "Requested",    dataIndex: "requested_at", key: "requested_at", width: 120, render: fmtDate },
    ...(showActions ? [{
      title: "Actions", key: "actions", width: 200,
      render: (_, r) => (
        <Space size={6}>
          <Button size="small" type="primary" danger
            disabled={r.status !== "PENDING"}
            icon={<AlertOutlined />}
            onClick={() => { setDispatchTarget(r); setSelectedUnitId(null); }}>
            Dispatch
          </Button>
          <Button size="small"
            onClick={() => setDetailTarget(r)}>
            Details
          </Button>
        </Space>
      ),
    }] : []),
  ];

  // ── views ─────────────────────────────────────────────────────────────────────

  function renderContent() {
    switch (selectedMenu) {

      case "dashboard":
        return (
          <>
            <Alert
              type="error"
              showIcon
              icon={<AlertOutlined />}
              message={<Text strong style={{ color: "#f5222d" }}>EMS Dispatch Center</Text>}
              description="Monitor and respond to emergency requests promptly. Active requests auto-refresh every 30 seconds."
              style={{ marginBottom: 24, borderRadius: 8 }}
            />

            {/* Stats */}
            <Row gutter={[16,16]} style={{ marginBottom: 24 }}>
              {statCards.map(({ title, value, color }) => (
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card variant="borderless" style={{ borderRadius: 8 }}>
                    <Statistic
                      title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>{title}</Text>}
                      value={value}
                      styles={{ content: { color, fontSize: 28, fontWeight: 600 } }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Active Requests table */}
            <Row gutter={[16,16]} style={{ marginBottom: 24 }}>
              <Col xs={24}>
                <Card
                  variant="borderless" style={{ borderRadius: 8 }}
                  title={
                    <Space>
                      <Text strong>Active Emergency Requests</Text>
                      {activeCount > 0 && <Badge count={activeCount} style={{ backgroundColor: "#f5222d" }} />}
                    </Space>
                  }>
                  <Table
                    columns={requestColumns(true)}
                    dataSource={activeRequests.slice(0, 6)}
                    pagination={false}
                    locale={{ emptyText: <Empty description="No active emergency requests" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>

            {/* Ambulance Units grid */}
            <Row gutter={[16,16]} style={{ marginBottom: 24 }}>
              <Col xs={24}>
                <Card variant="borderless" style={{ borderRadius: 8 }}
                  title={<Text strong>Ambulance Units Status</Text>}
                  extra={<Button size="small" icon={<PlusOutlined />} onClick={() => setAddUnitOpen(true)}>Add Unit</Button>}>
                  {units.length === 0 ? (
                    <Empty description="No units registered" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <Row gutter={[12,12]}>
                      {units.map(unit => (
                        <Col xs={24} sm={12} md={8} lg={6} key={unit.id}>
                          <Card size="small" style={{
                            borderRadius: 8,
                            borderTop: `3px solid ${UNIT_STATUS_BORDER[unit.current_status] ?? "#d9d9d9"}`,
                          }}>
                            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                              <CarOutlined style={{ marginRight: 6, color: "#595959" }} />
                              {unit.unit_number}
                            </div>
                            <Tag color={UNIT_STATUS_COLOR[unit.current_status] ?? "default"}
                              style={{ marginBottom: 6 }}>
                              {unit.current_status?.replace("_", " ")}
                            </Tag>
                            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                              Plate: {unit.license_plate ?? "—"}
                            </div>
                            <div style={{ fontSize: 11, color: "#bfbfbf", marginTop: 2 }}>
                              Updated: {fmtDate(unit.last_updated)}
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </Card>
              </Col>
            </Row>

            {/* Quick Actions */}
            <Row gutter={[16,16]}>
              <Col xs={24}>
                <Card variant="borderless" style={{ borderRadius: 8 }} title={<Text strong>Quick Actions</Text>}>
                  <Space wrap>
                    <Button type="primary" danger icon={<AlertOutlined />}
                      onClick={() => setSelectedMenu("requests")}>
                      View All Requests
                    </Button>
                    <Button icon={<CarOutlined />} onClick={() => setSelectedMenu("units")}>
                      Manage Units
                    </Button>
                    <Button icon={<PlusOutlined />} onClick={() => setAddUnitOpen(true)}>
                      Add Ambulance Unit
                    </Button>
                  </Space>
                </Card>
              </Col>
            </Row>
          </>
        );

      case "requests":
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Active Emergency Requests</Title>
            {activeCount > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
                message={`${activeCount} request${activeCount !== 1 ? "s" : ""} awaiting dispatch. Respond promptly.`} />
            )}
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Table
                columns={requestColumns(true)}
                dataSource={activeRequests}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                locale={{ emptyText: <Empty description="No active emergency requests" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                size="small"
                scroll={{ x: 800 }}
              />
            </Card>
          </>
        );

      case "units":
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Ambulance Units</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={<Text strong>Fleet Status</Text>}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setAddUnitOpen(true)}>Add Unit</Button>}>
              {units.length === 0 ? (
                <Empty description="No ambulance units registered" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 32 }} />
              ) : (
                <Row gutter={[16,16]} style={{ marginTop: 8 }}>
                  {units.map(unit => (
                    <Col xs={24} sm={12} md={8} lg={6} key={unit.id}>
                      <Card size="small" style={{
                        borderRadius: 8,
                        borderTop: `3px solid ${UNIT_STATUS_BORDER[unit.current_status] ?? "#d9d9d9"}`,
                      }}>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
                          <CarOutlined style={{ marginRight: 6 }} />
                          {unit.unit_number}
                        </div>
                        <Tag color={UNIT_STATUS_COLOR[unit.current_status] ?? "default"}
                          style={{ marginBottom: 8, fontSize: 12 }}>
                          {unit.current_status?.replace("_", " ")}
                        </Tag>
                        <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>
                          Plate: {unit.license_plate ?? "—"}
                        </div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 10 }}>
                          Updated: {fmtTime(unit.last_updated)}
                        </div>
                        {unit.current_status === "OUT_OF_SERVICE" && (
                          <Button size="small" icon={<CheckCircleOutlined />} style={{ width: "100%" }}
                            onClick={() => handleUnitStatusChange(unit, "AVAILABLE")}>
                            Mark Available
                          </Button>
                        )}
                        {unit.current_status === "AVAILABLE" && (
                          <Button size="small" danger icon={<CloseCircleOutlined />} style={{ width: "100%" }}
                            onClick={() => handleUnitStatusChange(unit, "OUT_OF_SERVICE")}>
                            Out of Service
                          </Button>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card>
          </>
        );

      case "logs":
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Transport Logs</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Table
                columns={LOG_COLS}
                dataSource={transportLogs}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                locale={{ emptyText: "No transport logs" }}
                size="small"
              />
            </Card>
          </>
        );

      case "profile":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>My Profile</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              {emsProfile ? (
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Email" span={2}>{emsProfile.user_email ?? user.email}</Descriptions.Item>
                  <Descriptions.Item label="Badge No.">{emsProfile.badge_number ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Role Type">
                    <Tag color={emsProfile.role_type === "DISPATCHER" ? "blue" : "purple"}>
                      {emsProfile.role_type ?? "—"}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Station">{emsProfile.station_name ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{emsProfile.phone ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    {emsProfile.is_active
                      ? <Tag color="green">Active</Tag>
                      : <Tag color="red">Inactive</Tag>}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <Text type="secondary">No EMS profile found. Contact your administrator.</Text>
              )}
            </Card>
            <ChangePasswordForm />
          </>
        );

      case "support":
        return (
          <Spin spinning={supportLoading}>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Support</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }} title={<Text strong>My Tickets</Text>}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setTicketModal(true)}>Report an Issue</Button>}>
              <Table size="small" dataSource={myTickets} pagination={{ pageSize: 10, hideOnSinglePage: true }}
                locale={{ emptyText: "No tickets yet" }}
                columns={[
                  { title:"ID",       dataIndex:"id",       key:"id",       width:90, render:id=><Text code style={{fontSize:11}}>{String(id).slice(0,8)}</Text> },
                  { title:"Subject",  dataIndex:"subject",  key:"subject",  ellipsis:true },
                  { title:"Priority", dataIndex:"priority", key:"priority", width:100, render:p=><Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[p]??"default"}>{p}</Tag> },
                  { title:"Status",   dataIndex:"status",   key:"status",   width:110, render:s=><Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default"}[s]??"default"}>{s?.replace(/_/g," ")}</Tag> },
                  { title:"Created",  dataIndex:"created_at",key:"created_at",width:110, render:fmtDate },
                  { title:"Actions",  key:"actions",        width:80, render:(_,r)=><Button size="small" onClick={()=>setTicketDetail(r)}>View</Button> },
                ]}
              />
            </Card>
          </Spin>
        );

      default: return null;
    }
  }

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}
        style={{ background: "#fff", borderRight: "1px solid #f0f0f0" }}>
        <div style={{
          height: 64, display: "flex", alignItems: "center", justifyContent: "center",
          borderBottom: "1px solid #f0f0f0", padding: "0 16px",
        }}>
          {!collapsed && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
              <Text strong style={{ color: "#f5222d", fontSize: 13, whiteSpace: "nowrap" }}>Digital Healthcare Network</Text>
              <Text style={{ color: "#f5222d", fontSize: 12, whiteSpace: "nowrap" }}>Emergency</Text>
            </div>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedMenu]}
          onClick={({ key }) => setSelectedMenu(key)}
          style={{ border: "none", marginTop: 8 }}
          items={MENU_ITEMS.map(item =>
            item.key === "requests"
              ? {
                  ...item,
                  label: (
                    <span>
                      Active Requests{" "}
                      {activeCount > 0 && (
                        <Badge count={activeCount} style={{ backgroundColor: "#f5222d", marginLeft: 6 }} />
                      )}
                    </span>
                  ),
                }
              : item
          )}
        />
      </Sider>

      <Layout>
        <Header style={{
          background: "#fff", padding: "0 24px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: "1px solid #f0f0f0",
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <div>
            <Text strong style={{ fontSize: 16, color: "#f5222d" }}>MCHUSA</Text>
            <Text style={{ fontSize: 14, color: "#8c8c8c", marginLeft: 8 }}>| Emergency Medical Services</Text>
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
            <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <Avatar size={32} icon={<UserOutlined />} style={{ background: "#f5222d" }} />
              <Text style={{ fontSize: 14, color: "#262626" }}>{user.email ?? "EMS Staff"}</Text>
            </div>
          </Dropdown>
        </Header>

        <Content style={{ padding: 24, background: "#f5f7fa" }}>
          {loading ? (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {[1, 2, 3, 4].map(i => (
                  <Col xs={24} sm={12} lg={6} key={i}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card variant="borderless" style={{ borderRadius: 8 }}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </Card>
            </>
          ) : error ? (
            <Alert type="error" message={error} showIcon style={{ marginBottom: 24 }}
              action={<Button size="small" onClick={() => setRetryCount(c => c + 1)}>Retry</Button>}
              onClose={() => setError(null)} closable />
          ) : renderContent()}
        </Content>
      </Layout>

      {/* ── New Ticket Modal ── */}
      <Modal title="Report an Issue" open={ticketModal}
        onOk={()=>ticketForm.submit()} onCancel={()=>{ setTicketModal(false); ticketForm.resetFields(); }}
        okText="Submit" destroyOnHidden keyboard>
        <Form form={ticketForm} layout="vertical" onFinish={handleCreateTicket}>
          <Form.Item name="subject" label="Subject" rules={[{required:true,message:"Required"}]}>
            <Input placeholder="Brief description of the issue" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{required:true,message:"Select priority"}]}>
            <Select placeholder="Select priority">
              <Option value="LOW">Low</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="HIGH">High</Option>
              <Option value="CRITICAL">Critical</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{required:true,message:"Required"}]}>
            <Input.TextArea rows={4} placeholder="Describe the issue in detail" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Ticket Detail Modal ── */}
      <Modal title="Ticket Details" open={Boolean(ticketDetail)}
        onCancel={()=>setTicketDetail(null)}
        footer={<Button onClick={()=>setTicketDetail(null)}>Close</Button>}
        destroyOnHidden keyboard width={500}>
        {ticketDetail && (
          <div>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <tbody>
                {[
                  ["Subject",  ticketDetail.subject],
                  ["Priority", <Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[ticketDetail.priority]}>{ticketDetail.priority}</Tag>],
                  ["Status",   <Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default"}[ticketDetail.status]}>{ticketDetail.status?.replace(/_/g," ")}</Tag>],
                  ["Created",  fmtTime(ticketDetail.created_at)],
                  ["Updated",  fmtTime(ticketDetail.updated_at)],
                ].map(([label,val])=>(
                  <tr key={label} style={{borderBottom:"1px solid #f0f0f0"}}>
                    <td style={{padding:"8px 12px",color:"#8c8c8c",width:"35%"}}>{label}</td>
                    <td style={{padding:"8px 12px"}}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{marginTop:12}}>
              <Text strong>Description</Text>
              <div style={{marginTop:6,padding:10,background:"#fafafa",borderRadius:4,fontSize:13,color:"#595959"}}>{ticketDetail.description}</div>
            </div>
            {ticketDetail.resolution_notes && (
              <div style={{marginTop:12}}>
                <Text strong>Resolution Notes</Text>
                <div style={{marginTop:6,padding:10,background:"#f6ffed",border:"1px solid #b7eb8f",borderRadius:4,fontSize:13}}>{ticketDetail.resolution_notes}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Dispatch Modal ── */}
      <Modal
        title={
          <Space>
            <AlertOutlined style={{ color: "#f5222d" }} />
            <span>Dispatch Ambulance</span>
          </Space>
        }
        open={Boolean(dispatchTarget)}
        onCancel={() => { setDispatchTarget(null); setSelectedUnitId(null); }}
        onOk={handleDispatch}
        okText="Confirm Dispatch"
        okButtonProps={{ danger: true, disabled: !selectedUnitId, loading: submitting }}
        cancelText="Cancel"
 keyboard
        destroyOnHidden>
        {dispatchTarget && (
          <>
            <Alert type="error" showIcon style={{ marginBottom: 16 }}
              message="Confirm before dispatching — an ambulance will be physically deployed." />
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Request ID">
                <Text code>{String(dispatchTarget.id).slice(0,8).toUpperCase()}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Pickup Address">{dispatchTarget.pickup_address ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Reason">{dispatchTarget.reason ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Requested At">{fmtTime(dispatchTarget.requested_at)}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Select Available Ambulance Unit:</Text>
            </div>
            {availableUnits.length === 0 ? (
              <Alert type="warning" showIcon message="No available units at this time. Mark a unit as Available first." />
            ) : (
              <Select
                style={{ width: "100%" }}
                placeholder="Choose a unit to dispatch"
                value={selectedUnitId}
                onChange={(value) => setSelectedUnitId(value)}>
                {availableUnits.map(u => (
                  <Select.Option key={u.id} value={u.id}>
                    {u.unit_number} — {u.license_plate ?? "No plate"}
                  </Select.Option>
                ))}
              </Select>
            )}
          </>
        )}
      </Modal>

      {/* ── Request Detail Modal ── */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            <span>Request Details — {detailTarget ? String(detailTarget.id).slice(0,8).toUpperCase() : ""}</span>
          </Space>
        }
        open={Boolean(detailTarget)}
        onCancel={() => setDetailTarget(null)}
        footer={
          detailTarget && ["PENDING","DISPATCHED"].includes(detailTarget.status) ? (
            <Space>
              <Button danger onClick={() => handleCancel(detailTarget)}>Cancel Request</Button>
              {detailTarget.status === "PENDING" && (
                <Button type="primary" danger icon={<AlertOutlined />}
                  onClick={() => { setDispatchTarget(detailTarget); setDetailTarget(null); setSelectedUnitId(null); }}>
                  Dispatch Now
                </Button>
              )}
              <Button onClick={() => setDetailTarget(null)}>Close</Button>
            </Space>
          ) : (
            <Button onClick={() => setDetailTarget(null)}>Close</Button>
          )
        }
        width={560}
 keyboard
        destroyOnHidden>
        {detailTarget && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Status">
              <Tag color={REQUEST_STATUS_COLOR[detailTarget.status] ?? "default"}>{detailTarget.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Pickup Address">{detailTarget.pickup_address ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Destination">{detailTarget.destination_hospital ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Reason">{detailTarget.reason ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Notes">{detailTarget.notes ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Assigned Unit">
              {detailTarget.assigned_unit
                ? <Text code>{String(detailTarget.assigned_unit).slice(0,8)}</Text>
                : <Text type="secondary">Not assigned</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Requested At">{fmtTime(detailTarget.requested_at)}</Descriptions.Item>
            <Descriptions.Item label="Dispatched At">{fmtTime(detailTarget.dispatched_at)}</Descriptions.Item>
            <Descriptions.Item label="Completed At">{fmtTime(detailTarget.completed_at)}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* ── Add Ambulance Unit Modal ── */}
      <Modal
        title={<Space><CarOutlined /><span>Add Ambulance Unit</span></Space>}
        open={addUnitOpen}
        onOk={() => addUnitForm.submit()}
        onCancel={() => { setAddUnitOpen(false); addUnitForm.resetFields(); }}
        okText="Add Unit"
        confirmLoading={submitting}
 keyboard
        destroyOnHidden>
        <Form form={addUnitForm} layout="vertical" onFinish={handleAddUnit} style={{ marginTop: 8 }}>
          <Form.Item name="unit_number" label="Unit Number"
            rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. AMB-001" />
          </Form.Item>
          <Form.Item name="license_plate" label="License Plate"
            rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. ABC 1234" />
          </Form.Item>
          <Form.Item name="current_status" label="Initial Status" initialValue="AVAILABLE">
            <Select>
              <Option value="AVAILABLE">Available</Option>
              <Option value="OUT_OF_SERVICE">Out of Service</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
